import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { getGuestId } from '@/lib/api';
import { toast } from 'sonner';

const CartContext = createContext(null);

export function CartProvider({ children }) {
    const [cart, setCart] = useState({ items: [], total: 0 });
    const [cartOpen, setCartOpen] = useState(false);

    const fetchCart = useCallback(async () => {
        try {
            const gid = getGuestId();
            const { data } = await api.get(`/cart?guest_id=${gid}`);
            setCart(data);
        } catch {}
    }, []);

    useEffect(() => { fetchCart(); }, [fetchCart]);

    const addToCart = async (productId, quantity = 1, variant = '') => {
        try {
            const gid = getGuestId();
            await api.post(`/cart/add?guest_id=${gid}`, { product_id: productId, quantity, variant });
            await fetchCart();
            setCartOpen(true);
            toast.success('Added to cart');
        } catch {
            toast.error('Failed to add to cart');
        }
    };

    const updateCart = async (productId, quantity, variant = '') => {
        try {
            const gid = getGuestId();
            await api.put(`/cart/update?guest_id=${gid}`, { product_id: productId, quantity, variant });
            await fetchCart();
        } catch {}
    };

    const removeFromCart = async (productId, variant = '') => {
        try {
            const gid = getGuestId();
            await api.delete(`/cart/remove/${productId}?guest_id=${gid}&variant=${variant}`);
            await fetchCart();
            toast.success('Removed from cart');
        } catch {}
    };

    const clearCart = async () => {
        try {
            const gid = getGuestId();
            await api.delete(`/cart/clear?guest_id=${gid}`);
            await fetchCart();
        } catch {}
    };

    const cartCount = cart.items.reduce((sum, item) => sum + item.quantity, 0);

    return (
        <CartContext.Provider value={{ cart, cartCount, cartOpen, setCartOpen, addToCart, updateCart, removeFromCart, clearCart, fetchCart }}>
            {children}
        </CartContext.Provider>
    );
}

export const useCart = () => useContext(CartContext);
